<template>
  <!-- data-app="true" so Vuetify's overlay system (v-select menu, v-dialog
       etc.) finds an anchor when this page is rendered outside v-app. -->
  <div class="daemon-page" data-app="true">
    <v-container class="daemon-shell">
      <header class="daemon-header">
        <div>
          <img
            :src="logoUrl"
            alt="Phasi Charoen"
            class="daemon-brand"
            decoding="async"
          />
          <div class="daemon-kicker">
            <v-icon size="17" color="primary">mdi-database-sync</v-icon>
            <span>eTax FoxPro Daemon FoxPro Daemon</span>
            <span class="daemon-kicker__dot" aria-hidden="true"></span>
            <span class="daemon-kicker__step">ขั้นที่ 2 จาก 2</span>
          </div>
          <h1>จับคู่ daemon กับบริษัท</h1>
          <p>ตรวจสอบ worker จาก CLI แล้วเลือกบริษัทและ basePath ที่จะใช้กับ FoxPro Express</p>
        </div>

        <div v-if="currentUserEmail" class="daemon-account" aria-label="Current account">
          <span class="daemon-account__dot" />
          <span class="daemon-account__email">{{ currentUserEmail }}</span>
          <v-btn
            icon
            x-small
            class="tw-ml-2"
            :loading="loggingOut"
            :title="'ออกจากระบบ'"
            @click="logout"
          >
            <v-icon x-small>mdi-logout</v-icon>
          </v-btn>
        </div>
      </header>

      <div class="daemon-layout">
        <aside class="daemon-rail" aria-label="Daemon setup progress">
          <div class="daemon-rail__title">ขั้นตอนการเชื่อมต่อ</div>

          <div class="daemon-step daemon-step--done">
            <div class="daemon-step__marker">
              <v-icon dark size="16">mdi-check</v-icon>
            </div>
            <div>
              <div class="daemon-step__label">ส่ง token</div>
              <div class="daemon-step__meta">CLI ได้รับ token แล้ว</div>
            </div>
          </div>

          <div class="daemon-step daemon-step--active">
            <div class="daemon-step__marker">2</div>
            <div>
              <div class="daemon-step__label">จับคู่บริษัท</div>
              <div class="daemon-step__meta">ผูก worker นี้เข้ากับบริษัท</div>
            </div>
          </div>

          <div class="daemon-security-note">
            <v-icon size="18" color="success">mdi-shield-lock-outline</v-icon>
            <span>Config จะถูกเขียนไปยัง Firestore เฉพาะบริษัทที่เลือก</span>
          </div>
        </aside>

        <v-card class="daemon-panel" elevation="0">
          <div class="daemon-panel__header">
            <div class="daemon-panel__icon">
              <v-icon size="24" color="primary">mdi-link-variant</v-icon>
            </div>
            <div>
              <h2>ยืนยัน worker และผูกบริษัท</h2>
              <p>worker: <code class="daemon-inline-code">{{ workerId }}</code></p>
            </div>
          </div>

          <v-card-text class="daemon-panel__body">
            <!-- Success is latched so CLI cleanup cannot flip the page back
                 to the missing pendingWorker state after a successful bind. -->
            <transition name="daemon-fade">
              <div
                v-if="bound"
                class="daemon-state daemon-state--success"
                role="status"
                aria-live="polite"
              >
                <div class="daemon-state__icon daemon-state__icon--pulse">
                  <v-icon size="28" color="success">mdi-check</v-icon>
                </div>
                <div>
                  <h3>จับคู่เสร็จแล้ว</h3>
                  <p>กลับไปที่ CLI ได้เลย daemon เริ่มทำงานแล้ว</p>
                  <div class="daemon-state__hint">
                    <v-icon size="14" color="success">mdi-check-all</v-icon>
                    <span>การตั้งค่าครบ — สามารถปิดหน้านี้ได้</span>
                  </div>
                  <v-btn
                    outlined
                    small
                    color="success"
                    class="daemon-state__action tw-mt-3"
                    to="/daemon/manage"
                  >
                    <v-icon left small>mdi-cog-outline</v-icon>
                    จัดการบริษัทที่ผูกไว้
                  </v-btn>
                </div>
              </div>
            </transition>

            <div v-if="!bound && loading" class="daemon-loading" aria-live="polite">
              <v-progress-circular indeterminate color="primary" size="34" />
              <span>กำลังโหลดข้อมูล worker…</span>
            </div>

            <transition name="daemon-fade">
              <v-alert
                v-if="!bound && !loading && error"
                type="error"
                outlined
                dense
                class="daemon-alert"
                role="alert"
              >
                <div class="daemon-alert__body">
                  <span>{{ error }}</span>
                  <v-btn
                    text
                    x-small
                    color="error"
                    aria-label="ปิดข้อความ"
                    @click="error = null"
                  >
                    <v-icon x-small>mdi-close</v-icon>
                  </v-btn>
                </div>
              </v-alert>
            </transition>

            <template v-if="!bound && !loading && !error && worker">
              <div v-if="!ownsWorker" class="daemon-state daemon-state--error">
                <div class="daemon-state__icon daemon-state__icon--error">
                  <v-icon size="24" color="red darken-1">mdi-account-alert-outline</v-icon>
                </div>
                <div>
                  <h3>ไม่มีสิทธิ์จับคู่ daemon นี้</h3>
                  <p>เครื่องที่รัน CLI ต้องล็อกอินด้วยบัญชีเดียวกับที่คุณกำลังใช้อยู่</p>
                  <v-btn
                    outlined
                    small
                    color="primary"
                    class="tw-mt-3"
                    :loading="loggingOut"
                    @click="logout"
                  >
                    <v-icon left small>mdi-logout</v-icon>
                    สลับบัญชี
                  </v-btn>
                </div>
              </div>

              <template v-else>
                <div class="worker-info">
                  <div class="worker-info__item">
                    <div class="worker-info__label">
                      <v-icon size="16" color="primary">mdi-laptop</v-icon>
                      <span>Machine</span>
                    </div>
                    <div class="worker-info__value">{{ worker.machineName || '—' }}</div>
                  </div>

                  <div class="worker-info__item">
                    <div class="worker-info__label">
                      <v-icon size="16" color="cyan darken-2">mdi-microsoft-windows</v-icon>
                      <span>Platform</span>
                    </div>
                    <div class="worker-info__value">{{ worker.platform || '—' }}</div>
                  </div>

                  <div v-if="worker.capabilities && worker.capabilities.version" class="worker-info__item">
                    <div class="worker-info__label">
                      <v-icon size="16" color="green darken-2">mdi-package-variant</v-icon>
                      <span>CLI version</span>
                    </div>
                    <div class="worker-info__value">v{{ worker.capabilities.version }}</div>
                  </div>

                  <div v-if="worker.capabilities && worker.capabilities.foxpro" class="worker-info__item worker-info__item--wide">
                    <div class="worker-info__label">
                      <v-icon size="16" color="amber darken-3">mdi-folder-outline</v-icon>
                      <span>Express path</span>
                    </div>
                    <code class="worker-info__value worker-info__value--code">
                      {{ worker.capabilities.foxpro.path }}
                    </code>
                  </div>

                  <div v-if="detectedCompanies.length" class="worker-info__item worker-info__item--wide">
                    <div class="worker-info__label">
                      <v-icon size="16" color="pink darken-2">mdi-folder-multiple-outline</v-icon>
                      <span>Detected folders</span>
                      <small class="worker-info__label-hint">คลิกเพื่อไปยังแถว</small>
                    </div>
                    <div class="worker-info__chips">
                      <v-chip
                        v-for="f in detectedCompanies"
                        :key="f"
                        x-small
                        class="worker-info__chip"
                        :class="{ 'worker-info__chip--matched': hasRowForFolder(f) }"
                        :title="hasRowForFolder(f) ? 'ไปยังแถวที่ใช้ ' + f : 'เพิ่มแถวสำหรับ ' + f"
                        @click="jumpToFolder(f)"
                      >
                        <v-icon v-if="hasRowForFolder(f)" left size="12" color="green darken-2">mdi-check</v-icon>
                        {{ f }}
                      </v-chip>
                    </div>
                  </div>
                </div>

                <div
                  v-if="companiesLoading || companies.length > 0"
                  class="daemon-field-block"
                >
                  <div class="daemon-section-label">
                    <v-icon small color="primary">mdi-domain</v-icon>
                    <span>บริษัทและ basePath</span>
                    <small>หนึ่งแถวต่อหนึ่งบริษัท</small>
                  </div>

                  <div class="daemon-rows">
                    <div
                      v-for="(row, idx) in rows"
                      :key="row._key"
                      ref="rowGroup"
                      class="daemon-row-group"
                    >
                      <div class="daemon-row">
                        <v-autocomplete
                          v-model="row.companyId"
                          :items="companyOptionsFor(idx)"
                          outlined
                          hide-details
                          dense
                          prepend-inner-icon="mdi-domain"
                          label="Company"
                          :loading="companiesLoading"
                          :disabled="binding"
                          :menu-props="{ maxHeight: 320 }"
                          no-data-text="ไม่พบบริษัทที่ตรง"
                          class="daemon-input daemon-row__company"
                          @change="_onRowCompanyChange(idx)"
                        >
                          <template #item="{ item }">
                            <div class="company-option">
                              <span class="company-option__text">{{ item.text }}</span>
                              <span
                                v-if="item._chip"
                                :class="['company-option__chip', 'company-option__chip--' + item._chip.tone]"
                              >
                                {{ item._chip.label }}
                              </span>
                            </div>
                          </template>
                          <template #selection="{ item }">
                            <span class="company-option__text">{{ item.text }}</span>
                          </template>
                        </v-autocomplete>
                        <v-text-field
                          v-model="row.basePath"
                          outlined
                          hide-details
                          dense
                          prepend-inner-icon="mdi-folder-key-outline"
                          label="basePath"
                          placeholder="เช่น M522DS"
                          persistent-placeholder
                          :disabled="binding"
                          class="daemon-input daemon-row__basepath"
                        />
                        <v-btn
                          icon
                          small
                          class="daemon-row__remove"
                          :disabled="binding || rows.length <= 1"
                          :aria-label="`ลบแถวที่ ${idx + 1}`"
                          @click="removeRow(idx)"
                        >
                          <v-icon small>mdi-close</v-icon>
                        </v-btn>
                      </div>
                      <div
                        v-if="rowNote(idx)"
                        :class="['daemon-row__note', 'daemon-row__note--' + rowNote(idx).tone]"
                      >
                        <v-icon size="14" :color="rowNote(idx).iconColor">{{ rowNote(idx).icon }}</v-icon>
                        <span>{{ rowNote(idx).text }}</span>
                      </div>
                    </div>
                  </div>

                  <div class="daemon-add-buttons">
                    <v-btn
                      text
                      small
                      color="primary"
                      class="daemon-add-row"
                      :disabled="binding || !canAddRow"
                      @click="addRow"
                    >
                      <v-icon left small>mdi-plus</v-icon>
                      เพิ่มบริษัท
                    </v-btn>
                    <v-btn
                      v-if="remainingCount > 1"
                      text
                      small
                      color="primary"
                      class="daemon-add-row"
                      :disabled="binding"
                      @click="addAllRemaining"
                    >
                      <v-icon left small>mdi-playlist-plus</v-icon>
                      เพิ่มทุกบริษัทที่เหลือ ({{ remainingCount }})
                    </v-btn>
                  </div>

                  <p class="daemon-rows__hint">
                    basePath = โฟลเดอร์ย่อยใต้ <code>--foxpro-path</code> ที่ CLI ใช้อ่าน .dbf ของบริษัทนั้น
                  </p>
                </div>

                <div
                  v-if="!companiesLoading && companies.length === 0"
                  class="daemon-state daemon-state--warning"
                >
                  <div class="daemon-state__icon daemon-state__icon--warning">
                    <v-icon size="24" color="amber darken-2">mdi-toggle-switch-off-outline</v-icon>
                  </div>
                  <div>
                    <h3>ยังไม่มีบริษัทที่เปิด AP Automation</h3>
                    <p>ติดต่อ support เพื่อเปิดสิทธิ์ AP Automation ก่อน</p>
                  </div>
                </div>

                <div
                  v-if="companies.length > 0"
                  class="daemon-actionbar"
                  role="region"
                  aria-label="Bind action"
                >
                  <div class="daemon-actionbar__top">
                    <div class="daemon-actionbar__stats">
                      <v-icon
                        size="16"
                        :color="readyRowCount === rows.length && rows.length > 0 ? 'success' : 'primary'"
                      >
                        {{ readyRowCount === rows.length && rows.length > 0 ? 'mdi-check-circle' : 'mdi-progress-clock' }}
                      </v-icon>
                      <span>
                        <strong>{{ readyRowCount }}</strong> / {{ rows.length }} บริษัทพร้อมจับคู่
                      </span>
                    </div>
                    <div
                      v-if="validationIssues.length"
                      id="daemon-validation-issues"
                      class="daemon-actionbar__issues"
                      role="status"
                      aria-live="polite"
                    >
                      <v-icon size="14" color="amber darken-3">mdi-alert-circle-outline</v-icon>
                      <span>{{ validationIssues.join(' · ') }}</span>
                    </div>
                  </div>

                  <div
                    v-if="binding && bindProgress.total > 1"
                    class="daemon-actionbar__progress"
                    role="status"
                    aria-live="polite"
                  >
                    <v-progress-linear
                      :value="(bindProgress.done / bindProgress.total) * 100"
                      color="primary"
                      rounded
                      height="6"
                    />
                    <span>กำลังบันทึก {{ bindProgress.done }} / {{ bindProgress.total }} บริษัท…</span>
                  </div>

                  <v-btn
                    block
                    large
                    class="daemon-cta"
                    :loading="binding"
                    :disabled="!canBind"
                    :aria-describedby="!canBind && validationIssues.length ? 'daemon-validation-issues' : null"
                    @click="bind"
                  >
                    <v-icon left>mdi-link-variant-plus</v-icon>
                    จับคู่ ({{ rows.length }} บริษัท)
                  </v-btn>
                </div>
              </template>
            </template>

            <div
              v-if="!bound && !loading && !error && !worker"
              class="daemon-state"
            >
              <div class="daemon-state__icon daemon-state__icon--muted">
                <v-icon size="24" color="grey darken-1">mdi-clock-alert-outline</v-icon>
              </div>
              <div>
                <h3>ไม่พบ pendingWorker นี้</h3>
                <p>
                  อาจหมดอายุเกิน 10 นาที หรือยังไม่ได้รัน CLI กลับไปรัน
                  <code>eTax FoxPro Daemon-foxpro --foxpro-path …</code> ใหม่
                </p>
                <div class="daemon-state__hint daemon-state__hint--muted">
                  <v-icon size="14" color="grey darken-1">mdi-information-outline</v-icon>
                  <span>เปิดลิงก์จาก CLI อีกครั้งหลังรันคำสั่งใหม่</span>
                </div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </div>
    </v-container>
  </div>
</template>

<script>
import firebase from 'firebase/app';
import fc from '@/firebaseConfig';
import logoUrl from '@/assets/logo_phasi_charoen.svg';

export default {
  name: 'DaemonPair',
  data() {
    return {
      logoUrl,
      worker: null,
      loading: true,
      error: null,
      workerRef: null,
      workerCb: null,
      companies: [],
      companiesLoading: false,
      rows: [],
      nextRowKey: 1,
      binding: false,
      // Latched once the pendingWorker reaches status==='bound' (either via
      // our own bind() write or via the snapshot). Once true it stays true,
      // so the CLI removing the pendingWorker right after pairing can't flip
      // the page back to the "ไม่พบ pendingWorker" empty state.
      bound: false,
      loggingOut: false,
      // Re-evaluated every 30s so the "stale" classification (lastSeen older
      // than STALENESS_MS) flips without needing a fresh RTDB push.
      now: Date.now(),
      // Surfaced under the CTA while bind() commits Firestore batches so a
      // 100-company pair doesn't sit on an opaque spinner.
      bindProgress: { done: 0, total: 0 },
    };
  },
  computed: {
    workerId() {
      return this.$route.params.workerId;
    },
    currentUid() {
      const u = firebase.auth().currentUser;
      return u ? u.uid : null;
    },
    currentUserEmail() {
      const u = firebase.auth().currentUser;
      return u ? (u.email || u.uid) : null;
    },
    ownsWorker() {
      return this.worker && this.worker.daemonUserId === this.currentUid;
    },
    detectedCompanies() {
      if (!this.worker || !this.worker.capabilities) return [];
      const fp = this.worker.capabilities.foxpro;
      return (fp && Array.isArray(fp.companies)) ? fp.companies : [];
    },
    canBind() {
      if (this.rows.length === 0) return false;
      const seen = new Set();
      for (const r of this.rows) {
        const bp = (r.basePath || '').trim();
        if (!r.companyId || !bp) return false;
        if (seen.has(r.companyId)) return false;
        seen.add(r.companyId);
      }
      return true;
    },
    canAddRow() {
      return this.rows.length < this.companies.length;
    },
    remainingCount() {
      const picked = new Set(
        this.rows.map(r => r.companyId).filter(Boolean),
      );
      let n = 0;
      for (const c of this.companies) {
        if (!picked.has(c.companyID)) n++;
      }
      return n;
    },
    // Counts rows that would pass canBind on their own — useful as an
    // at-a-glance progress indicator next to the CTA when there are many.
    readyRowCount() {
      if (this.rows.length === 0) return 0;
      const dupes = new Map();
      for (const r of this.rows) {
        if (r.companyId) dupes.set(r.companyId, (dupes.get(r.companyId) || 0) + 1);
      }
      let ready = 0;
      for (const r of this.rows) {
        if (!r.companyId) continue;
        if (!(r.basePath || '').trim()) continue;
        if ((dupes.get(r.companyId) || 0) > 1) continue;
        ready++;
      }
      return ready;
    },
    // Plain-Thai list of why canBind is false. Surfaced next to the CTA so
    // the disabled state is never silent — critical when 100 rows make the
    // failing row hard to spot.
    validationIssues() {
      const issues = [];
      if (this.rows.length === 0) {
        issues.push('ยังไม่มีบริษัทที่จะจับคู่');
        return issues;
      }
      let missingCompany = 0;
      let missingBasePath = 0;
      const counts = new Map();
      for (const r of this.rows) {
        if (!r.companyId) missingCompany++;
        else if (!(r.basePath || '').trim()) missingBasePath++;
        if (r.companyId) counts.set(r.companyId, (counts.get(r.companyId) || 0) + 1);
      }
      let dupRows = 0;
      for (const n of counts.values()) if (n > 1) dupRows += n;
      if (missingCompany) issues.push('ยังต้องเลือกบริษัทอีก ' + missingCompany + ' แถว');
      if (missingBasePath) issues.push('ยังต้องกรอก basePath อีก ' + missingBasePath + ' แถว');
      if (dupRows) issues.push('มีบริษัทซ้ำ ' + dupRows + ' แถว');
      return issues;
    },
  },
  mounted() {
    if (!this.currentUid) {
      const redirect = encodeURIComponent(this.$route.fullPath);
      this.$router.replace(`/login?redirect=${redirect}`);
      return;
    }
    this.subscribeWorker();
    this.loadCompanies();
    this._nowTimer = setInterval(() => { this.now = Date.now(); }, 30000);
  },
  beforeDestroy() {
    this.unsubscribeWorker();
    this._teardownPresence();
    if (this._nowTimer) { clearInterval(this._nowTimer); this._nowTimer = null; }
  },
  methods: {
    subscribeWorker() {
      this.loading = true;
      const ref = fc.rtdb.ref(`pendingWorkers/${this.workerId}`);
      const cb = ref.on('value', (snap) => {
        const data = snap.val();
        if (data) {
          this.worker = data;
          if (data.status === 'bound') this.bound = true;
        } else if (!this.bound) {
          // Only treat a null snapshot as "not found" if we haven't already
          // observed a successful bind; otherwise it's just CLI cleanup.
          this.worker = null;
        }
        this.loading = false;
        this._initRowsIfReady();
      }, (err) => {
        this.error = `ไม่สามารถอ่าน pendingWorkers: ${err.message}`;
        this.loading = false;
      });
      this.workerRef = ref;
      this.workerCb = cb;
    },
    unsubscribeWorker() {
      if (this.workerRef && this.workerCb) {
        try { this.workerRef.off('value', this.workerCb); } catch (e) { /* */ }
      }
      this.workerRef = null;
      this.workerCb = null;
    },
    async loadCompanies() {
      const uid = this.currentUid;
      if (!uid) return;
      this.companiesLoading = true;
      try {
        const snap = await fc.db
          .collection('CompaniesUsers')
          .where('userID', '==', uid)
          .where('isAdmin', '==', true)
          .get();
        const candidates = [];
        snap.forEach((doc) => {
          const d = doc.data();
          if (d && d.companyID) candidates.push(d);
        });

        // Same filter as /daemon/link: only AP Automation companies are
        // valid daemon targets. Companies/{cid}/ProductList/PSC.isApAutomate.
        // Also fetch existing FoxproConfig.basePath so rows can prefill
        // when the operator picks a company.
        const enriched = await Promise.all(candidates.map(async (c) => {
          try {
            const [psSnap, cfgSnap] = await Promise.all([
              fc.companies.doc(c.companyID).collection('ProductList').doc('PSC').get(),
              fc.companies.doc(c.companyID).collection('FoxproConfig').doc('config').get(),
            ]);
            const product = psSnap.exists ? (psSnap.data() || {}) : {};
            if (product.isApAutomate !== true) return null;
            const cfg = cfgSnap.exists ? (cfgSnap.data() || {}) : {};
            const hasBinding = cfg.storageMode === 'daemon' && !!cfg.daemonWorkerId;
            return {
              ...c,
              _basePath: typeof cfg.basePath === 'string' ? cfg.basePath : '',
              // Declare every reactive field at creation time so Vue 2 picks
              // them up — presence + presenceLoaded are mutated later by the
              // RTDB subscription and must already exist on the object.
              _binding: hasBinding ? {
                storageMode: cfg.storageMode,
                daemonUserId: cfg.daemonUserId || null,
                daemonWorkerId: cfg.daemonWorkerId,
                presence: null,
                presenceLoaded: false,
              } : null,
            };
          } catch (e) {
            return null;
          }
        }));
        this.companies = enriched.filter(Boolean);
        this._subscribePresence();
      } catch (err) {
        this.error = `โหลดรายการบริษัทไม่สำเร็จ: ${err.message}`;
      } finally {
        this.companiesLoading = false;
        this._initRowsIfReady();
      }
    },
    _subscribePresence() {
      this._teardownPresence();
      const subs = [];
      const uid = this.currentUid;
      for (const c of this.companies) {
        if (!c._binding) continue;
        // Rules allow `workers/{uid}/...` reads only when uid === auth.uid,
        // so we can only check presence for bindings owned by this account.
        if (c._binding.daemonUserId !== uid) continue;
        // If the existing binding IS this worker, there's nothing to "warn"
        // about — skip the subscription entirely.
        if (c._binding.daemonWorkerId === this.workerId) continue;
        const path = 'workers/' + c._binding.daemonUserId + '/' + c._binding.daemonWorkerId;
        const ref = fc.rtdb.ref(path);
        const cb = ref.on('value', (snap) => {
          c._binding.presence = snap.val() || null;
          c._binding.presenceLoaded = true;
        }, () => {
          // Mark loaded on error too so the UI exits the "checking…" state
          // and falls through to "offline" instead of spinning forever.
          c._binding.presenceLoaded = true;
        });
        subs.push({ ref, cb });
      }
      this._presenceRefs = subs;
    },
    _teardownPresence() {
      const subs = this._presenceRefs || [];
      for (const s of subs) {
        try { s.ref.off('value', s.cb); } catch (e) { /* */ }
      }
      this._presenceRefs = [];
    },
    companyStatus(c) {
      if (!c || !c._binding) return { kind: 'free' };
      const b = c._binding;
      if (b.daemonWorkerId && b.daemonWorkerId === this.workerId) return { kind: 'self' };
      if (!b.daemonUserId || b.daemonUserId !== this.currentUid) return { kind: 'bound-unknown' };
      if (!b.presenceLoaded) return { kind: 'bound-checking' };
      const presence = b.presence;
      if (!presence) return { kind: 'bound-offline' };
      // Mirrors cronDaemonHealth.js STALENESS_MS: 10 min past the last
      // heartbeat is the line between "live" and "stale".
      const STALENESS_MS = 10 * 60 * 1000;
      const lastSeen = Number(presence.lastSeen || 0);
      const ageMs = lastSeen > 0 ? this.now - lastSeen : null;
      if (presence.status === 'offline') return { kind: 'bound-offline', presence };
      if (ageMs !== null && ageMs > STALENESS_MS) return { kind: 'bound-stale', presence };
      return { kind: 'bound-online', presence };
    },
    _statusChip(status) {
      switch (status && status.kind) {
        case 'bound-online':   return { tone: 'warn',  label: 'ผูกอยู่ · ออนไลน์' };
        case 'bound-stale':    return { tone: 'muted', label: 'ผูกอยู่ · heartbeat ค้าง' };
        case 'bound-offline':  return { tone: 'muted', label: 'ผูกอยู่ · ออฟไลน์' };
        case 'bound-unknown':  return { tone: 'muted', label: 'ผูกอยู่ · บัญชีอื่น' };
        case 'bound-checking': return { tone: 'muted', label: 'กำลังตรวจสอบ…' };
        case 'self':
        case 'free':
        default:               return null;
      }
    },
    rowNote(idx) {
      const row = this.rows[idx];
      if (!row || !row.companyId) return null;
      const c = this.companies.find(x => x.companyID === row.companyId);
      if (!c) return null;
      const status = this.companyStatus(c);
      switch (status.kind) {
        case 'bound-online':
          return {
            tone: 'warn',
            icon: 'mdi-alert-circle-outline',
            iconColor: 'amber darken-3',
            text: 'บริษัทนี้ผูกกับ daemon อื่นที่ออนไลน์อยู่ — การจับคู่จะเขียนทับการตั้งค่าเดิม',
          };
        case 'bound-stale':
          return {
            tone: 'muted',
            icon: 'mdi-information-outline',
            iconColor: 'grey darken-1',
            text: 'บริษัทนี้ผูกกับ daemon เก่าที่ heartbeat ค้าง — จับคู่จะแทนที่ของเดิม',
          };
        case 'bound-offline':
          return {
            tone: 'muted',
            icon: 'mdi-information-outline',
            iconColor: 'grey darken-1',
            text: 'บริษัทนี้ผูกกับ daemon เก่าที่ออฟไลน์ — จับคู่จะแทนที่ของเดิม',
          };
        case 'bound-unknown':
          return {
            tone: 'muted',
            icon: 'mdi-information-outline',
            iconColor: 'grey darken-1',
            text: 'บริษัทนี้ผูกกับ daemon ของผู้ใช้บัญชีอื่น — ไม่สามารถตรวจสอบสถานะ',
          };
        case 'bound-checking':
          return {
            tone: 'muted',
            icon: 'mdi-progress-clock',
            iconColor: 'grey darken-1',
            text: 'กำลังตรวจสอบสถานะ daemon เดิม…',
          };
        case 'self':
        case 'free':
        default:
          return null;
      }
    },
    companyOptionsFor(idx) {
      const picked = new Set(
        this.rows
          .map((r, i) => (i === idx ? null : r.companyId))
          .filter(Boolean),
      );
      return this.companies.map((c) => {
        const status = this.companyStatus(c);
        return {
          text: this._companyLabel(c),
          value: c.companyID,
          disabled: picked.has(c.companyID),
          _chip: this._statusChip(status),
        };
      });
    },
    _newRow(overrides) {
      return {
        _key: this.nextRowKey++,
        companyId: null,
        basePath: '',
        ...(overrides || {}),
      };
    },
    _initRowsIfReady() {
      // Only ever populate once. Subsequent worker snapshots (or a manual
      // re-load) must not blow away the operator's in-progress edits.
      if (this.rows.length > 0) return;
      if (!this.worker || this.companiesLoading) return;
      // No AP-Automation companies → leave rows empty so the warning state
      // renders alone. canBind stays false on an empty rows array.
      if (this.companies.length === 0) return;

      // Always start with a single empty row. Detected folders are shown as
      // chips above; the operator clicks one (or "เพิ่มทุกบริษัทที่เหลือ") to
      // add rows on demand. Auto-creating one row per detected folder doesn't
      // scale — servers with hundreds of basepaths would produce hundreds of
      // rows the operator never wanted to pair.
      this.rows = [this._newRow()];
    },
    addRow() {
      this.rows.push(this._newRow());
    },
    hasRowForFolder(folder) {
      const fl = (folder || '').toLowerCase();
      if (!fl) return false;
      return this.rows.some(r => (r.basePath || '').toLowerCase() === fl);
    },
    async jumpToFolder(folder) {
      const fl = (folder || '').toLowerCase();
      let idx = this.rows.findIndex(r => (r.basePath || '').toLowerCase() === fl);
      if (idx === -1) {
        // Detected folder has no row (operator removed it). Re-add one,
        // swapping out the lone empty placeholder if that's all we had.
        const r0 = this.rows[0];
        const onlyEmpty = this.rows.length === 1 && r0
          && !r0.companyId && !(r0.basePath || '').trim();
        if (onlyEmpty) this.rows = [];
        const match = this.companies.find(c => (c._basePath || '').toLowerCase() === fl);
        this.rows.push(this._newRow({
          companyId: match ? match.companyID : null,
          basePath: folder,
        }));
        idx = this.rows.length - 1;
      }
      await this.$nextTick();
      this._highlightRow(idx);
    },
    _highlightRow(idx) {
      const refs = this.$refs.rowGroup;
      const el = Array.isArray(refs) ? refs[idx] : null;
      if (!el) return;
      if (typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      // Re-trigger the flash animation by toggling the class with a layout
      // read in between, so a rapid second click flashes again.
      el.classList.remove('daemon-row-group--flash');
      // eslint-disable-next-line no-unused-expressions
      el.offsetWidth;
      el.classList.add('daemon-row-group--flash');
      const row = this.rows[idx];
      // If the company is already picked, the operator's likely next edit is
      // the basePath; otherwise focus the company picker.
      const sel = row && row.companyId
        ? '.daemon-row__basepath input'
        : '.daemon-row__company input';
      const input = el.querySelector(sel);
      if (input && typeof input.focus === 'function') input.focus();
    },
    addAllRemaining() {
      const picked = new Set(
        this.rows.map(r => r.companyId).filter(Boolean),
      );
      const remaining = this.companies.filter(c => !picked.has(c.companyID));
      if (remaining.length === 0) return;
      // If the only existing row is the empty placeholder from
      // _initRowsIfReady (no detected folders), replace it instead of leaving
      // an empty row above the bulk-added ones.
      if (this.rows.length === 1) {
        const r0 = this.rows[0];
        if (r0 && !r0.companyId && !(r0.basePath || '').trim()) {
          this.rows = [];
        }
      }
      for (const c of remaining) {
        this.rows.push(this._newRow({
          companyId: c.companyID,
          basePath: c._basePath || '',
        }));
      }
    },
    removeRow(idx) {
      if (this.rows.length <= 1) return;
      this.rows.splice(idx, 1);
    },
    _onRowCompanyChange(idx) {
      const row = this.rows[idx];
      if (!row || !row.companyId || (row.basePath || '').trim()) return;
      const c = this.companies.find(x => x.companyID === row.companyId);
      if (c && c._basePath) row.basePath = c._basePath;
    },
    _companyLabel(c) {
      const co = c.company || {};
      // Prefer Thai name; fall back to English; last resort the companyID.
      return co.nameTH || co.nameEN || c.companyID;
    },
    async logout() {
      this.loggingOut = true;
      try {
        await firebase.auth().signOut();
        const redirect = encodeURIComponent(this.$route.fullPath);
        this.$router.replace(`/login?redirect=${redirect}`);
      } catch (err) {
        this.error = `ออกจากระบบไม่สำเร็จ: ${err.message}`;
        this.loggingOut = false;
      }
    },
    async bind() {
      if (!this.canBind) return;
      this.binding = true;
      this.error = null;
      // Firestore's batched-write limit is 500 ops per commit. We chunk at
      // 400 to leave headroom and to surface progress for large pairings
      // (a 100-company batch is one chunk; 1000 companies → three chunks).
      const CHUNK = 400;
      this.bindProgress = { done: 0, total: this.rows.length };
      try {
        const baseUpdate = {
          storageMode: 'daemon',
          daemonUserId: this.currentUid,
          daemonWorkerId: this.workerId,
          updatedAt: new Date(),
        };

        const boundTo = [];
        for (let i = 0; i < this.rows.length; i += CHUNK) {
          const slice = this.rows.slice(i, i + CHUNK);
          const batch = fc.db.batch();
          for (const row of slice) {
            const basePath = (row.basePath || '').trim();
            const ref = fc.db
              .collection('Companies')
              .doc(row.companyId)
              .collection('FoxproConfig')
              .doc('config');
            batch.set(ref, { ...baseUpdate, basePath }, { merge: true });
            boundTo.push({ companyId: row.companyId, basePath });
          }
          await batch.commit();
          this.bindProgress = { done: boundTo.length, total: this.rows.length };
        }

        await fc.rtdb.ref(`pendingWorkers/${this.workerId}`).update({
          status: 'bound',
          boundTo,
          boundAt: Date.now(),
        });

        // Latch immediately so CLI cleanup cannot show a stale empty state.
        this.bound = true;
      } catch (err) {
        this.error = `จับคู่ไม่สำเร็จ: ${err.message}`;
      } finally {
        this.binding = false;
      }
    },
  },
};
</script>

<style scoped>
.daemon-page {
  min-height: 100vh;
  background:
    radial-gradient(1200px 400px at 50% -160px, #e0eaff 0%, transparent 70%),
    #f6f8fb;
  color: #0f172a;
  font-family: "Noto Sans Thai", "Noto Sans", Roboto, sans-serif;
  -webkit-font-smoothing: antialiased;
}

.daemon-page :deep(.v-btn:focus-visible),
.daemon-page :deep(.v-input--is-focused .v-input__slot) {
  outline: 2px solid #1d4ed8;
  outline-offset: 2px;
}

@media (prefers-reduced-motion: reduce) {
  .daemon-page *,
  .daemon-page *::before,
  .daemon-page *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}

.daemon-shell {
  max-width: 1040px;
  padding: 40px 20px 48px;
}

.daemon-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 24px;
}

.daemon-brand {
  display: block;
  height: 32px;
  width: auto;
  margin-bottom: 14px;
  user-select: none;
}

.daemon-kicker {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #1d4ed8;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0;
}

.daemon-kicker__dot {
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: #94a3b8;
}

.daemon-kicker__step {
  padding: 2px 8px;
  background: #eff6ff;
  border-radius: 999px;
  color: #1d4ed8;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.2px;
}

.daemon-header h1 {
  margin: 8px 0 6px;
  color: #0f172a;
  font-size: 28px;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: 0;
}

.daemon-header p {
  max-width: 620px;
  margin: 0;
  color: #475569;
  font-size: 14px;
  line-height: 1.6;
}

.daemon-account {
  display: flex;
  align-items: center;
  flex: 0 1 auto;
  max-width: 340px;
  padding: 6px 8px 6px 12px;
  background: #fff;
  border: 1px solid #dbe3ee;
  border-radius: 999px;
  box-shadow: 0 8px 22px rgba(15, 23, 42, 0.06);
  font-size: 13px;
}

.daemon-account__dot {
  width: 8px;
  height: 8px;
  margin-right: 9px;
  border-radius: 50%;
  background: #16a34a;
}

.daemon-account__email {
  overflow: hidden;
  color: #0f172a;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.daemon-layout {
  display: grid;
  grid-template-columns: 300px minmax(0, 1fr);
  gap: 20px;
  align-items: start;
}

.daemon-rail,
.daemon-panel {
  background: #fff !important;
  border: 1px solid #dbe3ee;
  border-radius: 8px !important;
  box-shadow: 0 18px 45px rgba(15, 23, 42, 0.08) !important;
}

.daemon-rail {
  position: sticky;
  top: 24px;
  padding: 18px;
}

.daemon-rail__title {
  margin-bottom: 12px;
  color: #334155;
  font-size: 12px;
  font-weight: 700;
}

.daemon-step {
  position: relative;
  display: grid;
  grid-template-columns: 32px 1fr;
  gap: 12px;
  padding: 12px 0;
}

.daemon-step + .daemon-step::before {
  content: '';
  position: absolute;
  top: -12px;
  left: 15px;
  height: 24px;
  border-left: 2px solid #cbd5e1;
}

.daemon-step--done + .daemon-step::before {
  border-left-color: #16a34a;
}

.daemon-step__marker {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid #cbd5e1;
  border-radius: 50%;
  background: #fff;
  color: #64748b;
  font-size: 13px;
  font-weight: 700;
}

.daemon-step--active .daemon-step__marker {
  background: #1d4ed8;
  border-color: #1d4ed8;
  box-shadow: 0 0 0 4px rgba(29, 78, 216, 0.14);
  color: #fff;
}

.daemon-step--done .daemon-step__marker {
  background: #16a34a;
  border-color: #16a34a;
  color: #fff;
}

.daemon-step__label {
  color: #0f172a;
  font-size: 14px;
  font-weight: 700;
}

.daemon-step__meta {
  margin-top: 2px;
  color: #64748b;
  font-size: 12px;
  line-height: 1.5;
}

.daemon-security-note {
  display: grid;
  grid-template-columns: 20px 1fr;
  gap: 8px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #e2e8f0;
  color: #475569;
  font-size: 12px;
  line-height: 1.5;
}

/* Intentionally NOT setting overflow:hidden — any non-visible overflow on an
   ancestor breaks position:sticky on .daemon-actionbar. The actionbar carries
   its own bottom-corner radius so the panel's rounded edges still read clean. */

.daemon-panel__header {
  display: grid;
  grid-template-columns: 44px 1fr;
  gap: 14px;
  align-items: center;
  padding: 20px 22px;
  border-bottom: 1px solid #e2e8f0;
}

.daemon-panel__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 8px;
  background: #eff6ff;
}

.daemon-panel__header h2 {
  margin: 0;
  color: #0f172a;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0;
}

.daemon-panel__header p {
  margin: 4px 0 0;
  color: #64748b;
  font-size: 13px;
  line-height: 1.5;
}

.daemon-inline-code {
  color: #0f172a;
  font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
  font-size: 12px;
}

.daemon-panel__body {
  display: grid;
  gap: 18px;
  padding: 22px !important;
}

.daemon-alert {
  margin: 0 !important;
  border-radius: 8px !important;
}

.daemon-alert__body {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
}

.daemon-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: 160px;
  color: #475569;
  font-size: 14px;
}

.daemon-field-block {
  display: grid;
  gap: 9px;
}

.daemon-section-label {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #0f172a;
  font-size: 13px;
  font-weight: 700;
}

.daemon-section-label small {
  margin-left: auto;
  color: #64748b;
  font-size: 12px;
  font-weight: 500;
}

.daemon-input :deep(.v-input__slot) {
  min-height: 52px !important;
  border-radius: 8px !important;
}

.daemon-input :deep(.v-input__slot fieldset) {
  border-color: #cbd5e1 !important;
}

.daemon-input :deep(.v-label--active) {
  padding: 0 4px;
  background: #fff;
}

.daemon-input :deep(.v-text-field--outlined fieldset legend) {
  width: 0 !important;
  padding: 0 !important;
}

.daemon-rows {
  display: grid;
  gap: 10px;
}

.daemon-row-group {
  display: grid;
  gap: 6px;
}

.daemon-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) 36px;
  gap: 10px;
  align-items: center;
}

.daemon-row__note {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 12px;
  line-height: 1.45;
}

.daemon-row__note--warn {
  background: #fffbeb;
  border: 1px solid #fde68a;
  color: #92400e;
}

.daemon-row__note--muted {
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  color: #334155;
}

.company-option {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-width: 0;
}

.company-option__text {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.company-option__chip {
  flex: 0 0 auto;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.2px;
  white-space: nowrap;
}

.company-option__chip--warn {
  background: #fef3c7;
  color: #92400e;
}

.company-option__chip--muted {
  background: #e2e8f0;
  color: #475569;
}

.daemon-row__company,
.daemon-row__basepath {
  min-width: 0;
}

/* Vuetify's .v-select__selections defaults to flex-wrap: wrap, which pushes
   the (empty) inner autocomplete input onto a new line whenever the chosen
   company name is long enough to fill the row — leaving a visible blank line
   under the selection. Forcing nowrap keeps the truncated name and the input
   on a single row; .company-option__text already handles the ellipsis. */
.daemon-row__company :deep(.v-select__selections) {
  flex-wrap: nowrap;
}

.daemon-row__remove {
  justify-self: center;
}

.daemon-add-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  margin-top: 4px;
}

.daemon-add-row {
  text-transform: none !important;
  letter-spacing: 0 !important;
}

.daemon-actionbar {
  /* Sticks to viewport bottom while the panel content scrolls underneath,
     so the CTA + validation summary are always reachable at 100 rows.
     Returns to its natural position once we've scrolled past the panel. */
  position: sticky;
  bottom: 0;
  z-index: 5;
  display: grid;
  gap: 10px;
  /* Bleed to panel edges to clear the body's 22px padding. */
  margin: 0 -22px -22px;
  padding: 14px 22px 16px;
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(8px);
  border-top: 1px solid #e2e8f0;
  /* Match the panel's bottom rounding so the bar doesn't poke past corners
     (panel no longer clips via overflow:hidden — see note on .daemon-panel). */
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  box-shadow: 0 -8px 18px rgba(15, 23, 42, 0.05);
}

.daemon-actionbar__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px 14px;
  font-size: 13px;
  color: #334155;
}

.daemon-actionbar__stats {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
}

.daemon-actionbar__stats strong {
  color: #0f172a;
  font-weight: 800;
}

.daemon-actionbar__issues {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #92400e;
  font-size: 12px;
  font-weight: 600;
}

.daemon-actionbar__progress {
  display: grid;
  gap: 6px;
  color: #1e3a8a;
  font-size: 12px;
  font-weight: 600;
}

.daemon-rows__hint {
  margin: 4px 0 0;
  color: #64748b;
  font-size: 12px;
  line-height: 1.5;
}

.daemon-rows__hint code {
  padding: 1px 6px;
  background: #eef2f7;
  border-radius: 4px;
  font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
  font-size: 12px;
}

.daemon-state {
  display: grid;
  grid-template-columns: 42px 1fr;
  gap: 12px;
  align-items: start;
  padding: 16px;
  background: #f8fafc;
  border: 1px solid #dbe3ee;
  border-radius: 8px;
}

.daemon-state--success {
  background: #f0fdf4;
  border-color: #bbf7d0;
}

.daemon-state--warning {
  background: #fffbeb;
  border-color: #fde68a;
}

.daemon-state--error {
  background: #fef2f2;
  border-color: #fecaca;
}

.daemon-state__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 8px;
  background: #dcfce7;
}

.daemon-state__icon--muted {
  background: #e2e8f0;
}

.daemon-state__icon--warning {
  background: #fef3c7;
}

.daemon-state__icon--error {
  background: #fee2e2;
}

.daemon-state__icon--pulse {
  animation: daemon-pulse 1.6s ease-out 1;
}

.daemon-state__hint {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 10px;
  padding: 6px 10px;
  background: #dcfce7;
  border-radius: 999px;
  color: #166534;
  font-size: 12px;
  font-weight: 700;
}

.daemon-state__hint--muted {
  background: #e2e8f0;
  color: #334155;
}

@keyframes daemon-pulse {
  0% { box-shadow: 0 0 0 0 rgba(22, 163, 74, 0.45); }
  100% { box-shadow: 0 0 0 14px rgba(22, 163, 74, 0); }
}

.daemon-fade-enter-active,
.daemon-fade-leave-active {
  transition: opacity 200ms ease, transform 200ms ease;
}

.daemon-fade-enter,
.daemon-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

.daemon-state h3 {
  margin: 0;
  color: #0f172a;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.4;
}

.daemon-state p {
  margin: 4px 0 0;
  color: #475569;
  font-size: 13px;
  line-height: 1.6;
}

.worker-info {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.worker-info__item {
  min-width: 0;
  padding: 12px;
  background: #f8fafc;
  border: 1px solid #dbe3ee;
  border-radius: 8px;
}

.worker-info__item--wide {
  grid-column: 1 / -1;
}

.worker-info__label {
  display: flex;
  align-items: center;
  gap: 7px;
  color: #64748b;
  font-size: 12px;
  font-weight: 700;
}

.worker-info__value {
  margin-top: 7px;
  color: #0f172a;
  font-size: 13px;
  font-weight: 600;
  overflow-wrap: anywhere;
}

.worker-info__value--code {
  display: block;
  font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
  font-size: 12px;
  line-height: 1.5;
}

.worker-info__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.worker-info__chip {
  margin: 0 !important;
  background: #fff !important;
  border: 1px solid #cbd5e1 !important;
  color: #0f172a !important;
  font-weight: 700 !important;
  cursor: pointer;
}

.worker-info__chip--matched {
  background: #dcfce7 !important;
  border-color: #86efac !important;
  color: #166534 !important;
}

.daemon-page :deep(.v-chip:focus-visible) {
  outline: 2px solid #1d4ed8;
  outline-offset: 2px;
}

.worker-info__label-hint {
  margin-left: auto;
  color: #64748b;
  font-size: 11px;
  font-weight: 500;
}

.daemon-row-group--flash {
  border-radius: 8px;
  animation: daemon-row-flash 1.4s ease-out;
}

@keyframes daemon-row-flash {
  0%   { box-shadow: 0 0 0 3px rgba(29, 78, 216, 0.32); }
  100% { box-shadow: 0 0 0 0 rgba(29, 78, 216, 0); }
}

.daemon-cta {
  height: 48px !important;
  border-radius: 6px !important;
  background: #1d4ed8 !important;
  box-shadow: 0 12px 24px rgba(29, 78, 216, 0.22) !important;
  color: #fff !important;
  font-weight: 700 !important;
  letter-spacing: 0 !important;
}

.daemon-cta:hover:not(.v-btn--disabled) {
  background: #1e40af !important;
}

.daemon-cta.v-btn--disabled {
  background: #cbd5e1 !important;
  box-shadow: none !important;
  color: #fff !important;
}

@media (max-width: 780px) {
  .daemon-shell {
    padding: 28px 14px 36px;
  }

  .daemon-header {
    flex-direction: column;
  }

  .daemon-layout {
    grid-template-columns: 1fr;
  }

  .daemon-rail {
    position: static;
  }
}

@media (max-width: 620px) {
  .worker-info {
    grid-template-columns: 1fr;
  }

  .daemon-row {
    grid-template-columns: minmax(0, 1fr) 36px;
    grid-template-areas:
      "company remove"
      "basepath basepath";
  }

  .daemon-row__company { grid-area: company; }
  .daemon-row__remove { grid-area: remove; }
  .daemon-row__basepath { grid-area: basepath; }
}

@media (max-width: 520px) {
  .daemon-brand {
    height: 26px;
    margin-bottom: 10px;
  }

  .daemon-header h1 {
    font-size: 24px;
  }

  .daemon-account {
    width: 100%;
    max-width: none;
  }

  .daemon-panel__header,
  .daemon-panel__body {
    padding: 18px !important;
  }

  .daemon-actionbar {
    /* Body padding shrinks to 18px on mobile — match the bleed so the bar
       still hits the panel edges without poking past them. */
    margin: 0 -18px -18px;
    padding: 12px 18px 14px;
  }

  .daemon-section-label {
    align-items: flex-start;
    flex-wrap: wrap;
  }

  .daemon-section-label small {
    width: 100%;
    margin-left: 25px;
  }

  .daemon-state {
    grid-template-columns: 1fr;
  }
}
</style>
